import type { Project } from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { Cable, FolderKanban, FolderPlus, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateProject,
  useForgetProject,
  useProjects,
  useRefreshCatalogue,
} from "../api/queries.js";
import { useIsAdmin } from "../api/users.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog } from "../ui/overlay.js";
import { Badge, Button, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";

const STATUS_TONE = { active: "ok", failing: "warn", revoked: "danger" } as const;

/** Status, permission and token hint of a project's provider connection. */
export function ConnectionBadges({ project }: { project: Project }) {
  const { t } = useTranslation();
  const c = project.connection;
  if (!c) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge tone={STATUS_TONE[c.status]}>{t(`projects.status.${c.status}`)}</Badge>
      <Badge>{t(`projects.permission.${c.permission}`)}</Badge>
    </span>
  );
}

/** Form that connects a provider project by its token; used in a dialog and on first run. */
export function ConnectProjectForm({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation();
  const create = useCreateProject();
  const navigate = useNavigate();
  const toast = useToast();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate(
      {
        name: String(form.get("name") ?? "").trim(),
        provider: "hetzner",
        credential: String(form.get("credential") ?? "").trim(),
      },
      {
        onSuccess: (project) => {
          toast({ tone: "ok", title: t("projects.connected", { name: project.name }) });
          onDone?.();
          void navigate({ to: "/projects/$projectId", params: { projectId: project.id } });
        },
      },
    );
  };
  return (
    <form id="connect-project" onSubmit={submit} className="flex flex-col gap-4">
      <TextField
        label={t("projects.name")}
        name="name"
        required
        maxLength={64}
        hint={t("projects.nameHint")}
        error={fieldError(create.error, "name", t)}
      />
      <TextField
        label={t("projects.token")}
        name="credential"
        type="password"
        required
        autoComplete="off"
        spellCheck={false}
        hint={t("projects.tokenHelp")}
        info={<FieldHint text={t("projects.tokenWhy")} topic="projects" />}
        error={fieldError(create.error, "credential", t)}
      />
      <ErrorMessage error={create.error} />
      <div className="flex justify-end gap-2">
        {onDone && (
          <Button variant="secondary" onClick={onDone}>
            {t("common.cancel")}
          </Button>
        )}
        <Button
          type="submit"
          busy={create.isPending}
          icon={<Cable className="size-4" aria-hidden />}
        >
          {t("projects.submit")}
        </Button>
      </div>
    </form>
  );
}

/** Removal dialog shared by the projects list and the connection page. */
export function ForgetProjectDialog({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const forget = useForgetProject(project.id);
  const navigate = useNavigate();
  const toast = useToast();
  return (
    <DangerDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("projects.forgetTitle", { name: project.name })}
      consequences={[
        t("projects.forgetToken"),
        t("projects.forgetKeep"),
        t("projects.forgetBilling"),
      ]}
      confirmText={project.name}
      actionLabel={t("projects.forget")}
      busy={forget.isPending}
      // mutateAsync, not mutate: live updates can refetch the removed project, turn the page
      // into "project not found" and unmount this dialog, and mutate's callbacks die with it.
      onConfirm={() =>
        void forget.mutateAsync().then(
          () => {
            onOpenChange(false);
            toast({ tone: "ok", title: t("projects.forgotten", { name: project.name }) });
            void navigate({ to: "/" });
          },
          () => toast({ tone: "danger", title: t("common.unexpected") }),
        )
      }
    />
  );
}

function ProjectName({ project }: { project: Project }) {
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      className="font-medium text-text hover:text-accent"
    >
      {project.name}
    </Link>
  );
}

export function ProjectsPage({
  connect = false,
  onConnectClosed,
}: {
  /** Opens «Подключить проект» at once (the switcher's «+ Новый проект»). */
  connect?: boolean;
  onConnectClosed?: () => void;
} = {}) {
  const { t, i18n } = useTranslation();
  const projects = useProjects();
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const [forgetting, setForgetting] = useState<Project | null>(null);
  const refresh = useRefreshCatalogue();
  const toast = useToast();
  // Connecting and removing projects is for administrators (docs/users.md).
  const admin = useIsAdmin();
  useEffect(() => {
    if (connect && admin) setConnecting(true);
  }, [connect, admin]);
  const changeConnecting = (open: boolean) => {
    setConnecting(open);
    // The address asked for the dialog; closing it leaves the plain list behind.
    if (!open && connect) onConnectClosed?.();
  };
  const refreshing = (p: Project) =>
    refresh.mutate(p.id, {
      onSuccess: () => toast({ tone: "ok", title: t("projects.refreshed", { name: p.name }) }),
      onError: () => toast({ tone: "danger", title: t("common.unexpected") }),
    });

  const connectButton = admin ? (
    <Button
      onClick={() => changeConnecting(true)}
      icon={<FolderPlus className="size-4" aria-hidden />}
    >
      {t("projects.add")}
    </Button>
  ) : undefined;

  return (
    <>
      <PageHeader
        title={t("projects.title")}
        intro={t("projects.intro")}
        help="projects"
        actions={projects.data?.length ? connectButton : undefined}
      />
      {projects.error ? (
        <ErrorMessage error={projects.error} />
      ) : (
        <DataTable
          label={t("projects.title")}
          rows={projects.data}
          rowKey={(p) => p.id}
          initialSort={{ key: "name", dir: "asc" }}
          empty={
            <EmptyState
              icon={<FolderKanban className="size-6" aria-hidden />}
              title={admin ? t("projects.empty") : t("projects.emptyMember")}
              action={connectButton}
              help="projects"
            >
              {admin ? t("projects.emptyLead") : t("projects.emptyMemberLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("projects.name"),
              primary: true,
              sort: (p) => p.name.toLowerCase(),
              cell: (p) => <ProjectName project={p} />,
            },
            {
              key: "status",
              header: t("projects.state"),
              cell: (p) => <ConnectionBadges project={p} />,
            },
            {
              key: "token",
              header: t("projects.token"),
              hideOnPhone: true,
              cell: (p) =>
                p.connection ? (
                  <span className="font-mono text-muted">…{p.connection.credentialHint}</span>
                ) : (
                  "—"
                ),
            },
            {
              key: "synced",
              header: t("projects.catalogue"),
              sort: (p) => p.connection?.lastSyncAt ?? "",
              cell: (p) =>
                p.connection?.lastSyncAt ? (
                  <span className="text-muted">
                    {relativeTime(p.connection.lastSyncAt, i18n.language)}
                  </span>
                ) : (
                  <span className="text-muted">{t("projects.neverSynced")}</span>
                ),
            },
          ]}
          rowMenu={(p) => [
            [
              {
                label: t("projects.open"),
                icon: <FolderKanban className="size-4" aria-hidden />,
                onSelect: () =>
                  void navigate({ to: "/projects/$projectId", params: { projectId: p.id } }),
              },
              {
                label: t("projects.refresh"),
                icon: <RefreshCw className="size-4" aria-hidden />,
                onSelect: () => refreshing(p),
              },
              {
                label: t("nav.connection"),
                icon: <Cable className="size-4" aria-hidden />,
                onSelect: () =>
                  void navigate({
                    to: "/projects/$projectId/connection",
                    params: { projectId: p.id },
                  }),
              },
            ],
            ...(p.access === "admin"
              ? [
                  [
                    {
                      label: t("projects.forget"),
                      icon: <Trash2 className="size-4" aria-hidden />,
                      danger: true,
                      onSelect: () => setForgetting(p),
                    },
                  ],
                ]
              : []),
          ]}
        />
      )}
      <Dialog
        open={connecting}
        onOpenChange={changeConnecting}
        title={t("projects.add")}
        description={t("projects.addLead")}
      >
        <ConnectProjectForm onDone={() => changeConnecting(false)} />
      </Dialog>
      {forgetting && (
        <ForgetProjectDialog
          project={forgetting}
          open
          onOpenChange={(open) => !open && setForgetting(null)}
        />
      )}
    </>
  );
}
